import {
    AnyProduct,
    IProductBook,
    IProductGiftCard,
    IProductIssueInfo,
    IProductPublicationInfo,
    IProductPublicationSubscription,
    IProductRegularSubscription,
    IProductSingleIssue,
    isProductTrial,
} from '@pressreader/commerce-types';
import { extractDate } from '@pressreader/content-utils';

import {
    AnyPurchaseProductDto,
    IBookProductDto,
    IGiftCardProductDto,
    IPublicationSubscriptionDto,
    IRegularSubscriptionDto,
    ISingleIssueDto,
} from '../types/product';

function mapFromProductDto(dto: IRegularSubscriptionDto): IProductRegularSubscription;
function mapFromProductDto(dto: IPublicationSubscriptionDto): IProductPublicationSubscription;
function mapFromProductDto(dto: ISingleIssueDto): IProductSingleIssue;
function mapFromProductDto(dto: IGiftCardProductDto): IProductGiftCard;
function mapFromProductDto(dto: IBookProductDto): IProductBook;
function mapFromProductDto(dto: AnyPurchaseProductDto): AnyProduct;
function mapFromProductDto(dto: AnyPurchaseProductDto): AnyProduct | null {
    if (!dto) {
        return null;
    }

    const product = {
        token: dto.token,
        title: dto.title,

        callToAction: dto.callToAction,
        highlight: dto.highlight,
        features: dto.features || [],

        tags: dto.tags,

        currency: dto.currency,
        price: dto.price,
        formattedPrice: dto.formattedPrice,
        discount: dto.discount,
        formattedDiscount: dto.formattedDiscount,
        total: dto.total,
        formattedTotal: dto.formattedTotal,
        // Important: `issueIncluded` is true by default (when missing or true).
        // Only an explicit `false` means it is not included.
        // DO NOT replace with `!!product.issueIncluded` — it would incorrectly treat `undefined` as false.

        issueIncluded: dto.issueIncluded !== false,

        isTrial: isProductTrial(dto),
    };

    if (dto.type === 'RegularSubscription') {
        return {
            ...product,
            type: 'RegularSubscription',

            productId: dto.productId,
            term: { duration: dto.term.duration, units: dto.term.units },
            autoRenewEnabled: dto.autoRenewEnabled,
            autoRenewToProduct: mapFromProductDto(dto.autoRenewToProduct),
        };
    }

    if (dto.type === 'PublicationSubscription') {
        return {
            ...product,
            type: 'PublicationSubscription',

            productId: dto.productId,
            term: { duration: dto.term.duration, units: dto.term.units },
            autoRenewEnabled: dto.autoRenewEnabled,
            autoRenewToProduct: mapFromProductDto(dto.autoRenewToProduct),

            cids: dto.cids,

            publicationsInfo: [],
        };
    }

    if (dto.type === 'SingleIssue') {
        return {
            ...product,
            type: 'SingleIssue',

            issueKey: dto.issueKey,
            cid: dto.cid,

            issueInfo: null,
        };
    }

    if (dto.type === 'GiftCard') {
        return {
            ...product,
            type: 'GiftCard',

            productId: dto.productId,
        };
    }

    if (dto.type === 'Book') {
        return {
            ...product,
            type: 'Book',

            bookId: dto.bookId,
            productId: dto.productId,
            bookInfo: null,
            autoRenewEnabled: dto.autoRenewEnabled,
            salesModelId: dto.salesModelId,
            supplyModelId: dto.supplyModelId,
        };
    }

    return null;
}

function mapFromIssueInfoDto(issueInfoDto: {
    Issue: { Issue: string; CID: string; ExpungeVersion: number };
    Newspaper: { Name: string };
    CouldPotentiallyBeRead: boolean;
    SubscriptionsInfo: {
        IsFreeContent: boolean;
        RequiredAuthenticatedUser: boolean;
    };
}): IProductIssueInfo | null {
    if (!issueInfoDto) {
        return null;
    }
    const issueKey = issueInfoDto.Issue.Issue;
    const issueDate = extractDate(issueKey);

    return {
        cid: issueInfoDto.Issue.CID,
        issueKey,
        publicationName: issueInfoDto.Newspaper.Name,
        issueDate,
        expungeVersion: issueInfoDto.Issue.ExpungeVersion,
        couldBeRead: issueInfoDto.CouldPotentiallyBeRead,
        isFreeContent: issueInfoDto.SubscriptionsInfo.IsFreeContent,
        requiredAuthenticatedUser: issueInfoDto.SubscriptionsInfo.RequiredAuthenticatedUser,
    };
}

function mapFromPublicationDto(publicationDto: { cid: string; displayName: string }): IProductPublicationInfo | null {
    if (!publicationDto) {
        return null;
    }

    return {
        cid: publicationDto.cid,
        publicationName: publicationDto.displayName,
    };
}

export { mapFromIssueInfoDto, mapFromProductDto, mapFromPublicationDto };
